import { ChangeDetectorRef, Component, inject, Input, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { FormControl, ReactiveFormsModule } from '@angular/forms'
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { Actions, ofType } from '@ngrx/effects'
import { Store, select } from '@ngrx/store'
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed'
import { filter } from 'rxjs/operators'
import { ACCOUNT_ACTIVE } from '@tradecafe/types/core'
import { loadBuyersGroups, selectAllBuyersGroups, updateBuyersGroup, updateBuyersGroupFailure } from 'src/app/store/buyers-groups'
import { BuyersGroup } from 'src/pages/admin/settings/buyers-groups/buyers-groups-item/buyers-groups.service'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { DeepReadonly } from '@tradecafe/types/utils'

@Component({
  selector: 'tc-buyer-groups-picker',
  templateUrl: './buyer-groups-picker.component.html',
  styleUrls: ['./buyer-groups-picker.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    DragDropModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
})
export class BuyerGroupsPickerComponent extends OnDestroyMixin implements OnInit {
  private store = inject(Store)
  private actions$ = inject(Actions)
  private cdr = inject(ChangeDetectorRef)

  @Input({ required: true }) buyerId: string
  @Input() archived = false
  @Input() status: number
  // AI-37: trader/logistics can view the groups but not change them
  @Input() readonly = false

  availableGroups: DeepReadonly<BuyersGroup>[] = []
  assignedGroups: DeepReadonly<BuyersGroup>[] = []
  searchAvailable = new FormControl('')
  searchAssigned = new FormControl('')
  loading = true
  private initialized = false
  private prevAvailable: DeepReadonly<BuyersGroup>[] = []
  private prevAssigned: DeepReadonly<BuyersGroup>[] = []
  private lastUpdatedGroupId: string

  private toaster = inject(ToasterService)

  ngOnInit() {
    this.store.dispatch(loadBuyersGroups())
    this.store.pipe(
      select(selectAllBuyersGroups),
      untilComponentDestroyed(this),
    ).subscribe(groups => {
      if (this.initialized || !groups.length) return
      this.assignedGroups = groups.filter(g => g.buyers.includes(this.buyerId))
      this.availableGroups = groups.filter(g => !g.buyers.includes(this.buyerId) && !this.isShared(g))
      this.loading = false
      this.initialized = true
      this.cdr.markForCheck()
    })

    this.actions$.pipe(
      ofType(updateBuyersGroupFailure),
      filter(action => action.id === this.lastUpdatedGroupId),
      untilComponentDestroyed(this),
    ).subscribe(() => {
      this.availableGroups = this.prevAvailable
      this.assignedGroups = this.prevAssigned
      this.cdr.markForCheck()
    })
  }

  get filteredAvailable(): DeepReadonly<BuyersGroup>[] {
    return this.filterAndSort(this.availableGroups, this.searchAvailable.value)
  }

  isShared(group: DeepReadonly<BuyersGroup>): boolean {
    return !!group.shared_by && !group.is_forked
  }

  get filteredAssigned(): DeepReadonly<BuyersGroup>[] {
    return this.filterAndSort(this.assignedGroups, this.searchAssigned.value)
  }

  private filterAndSort(groups: DeepReadonly<BuyersGroup>[], term: string): DeepReadonly<BuyersGroup>[] {
    if (!term) return groups
    const t = term.toLowerCase()
    const filtered = groups.filter(g => g.name.toLowerCase().includes(t))
    return [...filtered].sort((a, b) => {
      const aStarts = a.name.toLowerCase().startsWith(t) ? 0 : 1
      const bStarts = b.name.toLowerCase().startsWith(t) ? 0 : 1
      return aStarts - bStarts || a.name.localeCompare(b.name)
    })
  }

  onDrop(event: CdkDragDrop<DeepReadonly<BuyersGroup>[]>) {
    if (event.previousContainer === event.container) return
    const group: DeepReadonly<BuyersGroup> = event.item.data
    const toAssigned = event.container.id === 'assigned-groups'
    this.moveGroup(group, toAssigned, event.currentIndex)
  }

  onClickMove(group: DeepReadonly<BuyersGroup>, toAssigned: boolean) {
    this.moveGroup(group, toAssigned)
  }

  private moveGroup(group: DeepReadonly<BuyersGroup>, toAssigned: boolean, index?: number) {
    if (this.readonly || this.isShared(group)) return
    if (toAssigned && this.archived) {
      this.toaster.error('Archived buyer cannot be added to a group.')
      return
    }
    if (toAssigned && this.status !== ACCOUNT_ACTIVE) {
      this.toaster.error('Only active buyers can be added to a group.')
      return
    }
    // Snapshot for rollback on failure
    this.prevAvailable = this.availableGroups
    this.prevAssigned = this.assignedGroups
    this.lastUpdatedGroupId = group.group_id

    // Optimistic update locally
    let updatedGroup: BuyersGroup
    if (toAssigned) {
      updatedGroup = { ...group, buyers: [...group.buyers, this.buyerId] }
      this.availableGroups = this.availableGroups.filter(g => g.group_id !== group.group_id)
      const i = index ?? this.assignedGroups.length
      this.assignedGroups = [...this.assignedGroups.slice(0, i), updatedGroup, ...this.assignedGroups.slice(i)]
    } else {
      updatedGroup = { ...group, buyers: group.buyers.filter(id => id !== this.buyerId) }
      this.assignedGroups = this.assignedGroups.filter(g => g.group_id !== group.group_id)
      const i = index ?? this.availableGroups.length
      this.availableGroups = [...this.availableGroups.slice(0, i), updatedGroup, ...this.availableGroups.slice(i)]
    }
    this.cdr.markForCheck()

    // Dispatch store action to persist
    this.store.dispatch(updateBuyersGroup({ buyersGroup: updatedGroup, id: updatedGroup.group_id }))
  }
}